// ─── FuelDeliveryReport — relatório de entrega das OT de distribuição de
// combustível (Move Express / Transfuel / clientes com serviceType 'fuel').
//
// Substitui o relatório genérico (OrderReportDialog) para OT fuel. Mostra os
// dados capturados no fluxo (carregamento + entrega + selo + medição) e gera
// um PDF estilo guia. NÃO tem email (removido a pedido). Estado da entrega:
// "Entrega Efetuada" (em vez de "Serviço Resolvido").
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import { Dialog, Box, Typography, IconButton, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import { jsPDF } from 'jspdf';

// Nome do fornecedor de combustível (para cabeçalho).
function fuelSupplierLabel(order) {
  const map = { sonangol: 'Sonangol', total: 'Total', pumangol: 'Pumangol', sonangalp: 'Sonangalp' };
  if (order?.fuelCustomer && map[order.fuelCustomer]) return map[order.fuelCustomer];
  const ref = order?.customRef || '';
  if (ref.startsWith('Tot-')) return 'Total';
  if (ref.startsWith('Pum-')) return 'Pumangol';
  if (ref.startsWith('Snp-')) return 'Sonangalp';
  return 'Sonangol';
}

const numFmt = (v) => (v != null && v !== '' ? Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 2 }) : null);

export default function FuelDeliveryReport({ open, order, onClose, lang = 'pt' }) {
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const { token } = useContext(AuthContext);
  // Foto ampliada (lightbox). null = nenhuma; { url, label } = a mostrar em grande.
  const [zoom, setZoom] = useState(null);
  // Zoom dentro do lightbox: escala + ponto de origem (onde o utilizador clicou).
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const closeZoom = () => { setZoom(null); setZoomScale(1); };
  // Fotos das guias (base64 pesado) já não vêm na lista — busca-se on-demand
  // quando o relatório abre e a OT tem foto (flags hasLoadingPhoto/…).
  const [photos, setPhotos] = useState(null);
  useEffect(() => {
    if (!open || !token || !order?.id) return undefined;
    if (!order.hasLoadingPhoto && !order.hasDeliveryPhoto) { setPhotos(null); return undefined; }
    if (order.loadingPhotoUrl || order.deliveryPhotoUrl) return undefined; // já vieram inline
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`/planner-api/transport-orders/${order.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancel) setPhotos({ loadingPhotoUrl: json.loadingPhotoUrl || null, deliveryPhotoUrl: json.deliveryPhotoUrl || null });
      } catch (_) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [open, order?.id, order?.hasLoadingPhoto, order?.hasDeliveryPhoto, order?.loadingPhotoUrl, order?.deliveryPhotoUrl, token]);
  if (!order) return null;
  // URLs efetivos (hidratadas > inline).
  const loadingPhotoUrl = photos?.loadingPhotoUrl || order.loadingPhotoUrl || null;
  const deliveryPhotoUrl = photos?.deliveryPhotoUrl || order.deliveryPhotoUrl || null;

  const supplier = fuelSupplierLabel(order);
  const loadedQty = order.loadingOrderQty != null ? Number(order.loadingOrderQty) : null;
  const unloadQty = order.deliveryGuideQty != null ? Number(order.deliveryGuideQty) : null;
  const divergence = (loadedQty != null && unloadQty != null) ? (loadedQty - unloadQty) : null;
  const sealIntact = order.deliverySealIntact;
  const otRef = order.customRef || order.orderNumber || `OT #${order.id}`;
  // Entrega dividida por 2 P.A. (Fase B) — a mesma cisterna descarrega em 2 postos.
  const isSplitOT = !!order.splitDelivery;
  const pa1Qty = order.plannedQty1 != null && order.plannedQty1 !== '' ? Number(order.plannedQty1) : null;
  const pa2Qty = order.plannedQty2 != null && order.plannedQty2 !== '' ? Number(order.plannedQty2) : null;

  // Linha de campo (label + valor).
  const Field = ({ label, value, mono }) => (
    <Box style={{ marginBottom: 8 }}>
      <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography style={{
        fontSize: '0.86rem', fontWeight: 600, color: value != null ? '#0f172a' : '#cbd5e1',
        fontFamily: mono ? 'ui-monospace, Menlo, monospace' : 'inherit',
      }}>{value != null && value !== '' ? value : '—'}</Typography>
    </Box>
  );

  const Section = ({ title, children, cols = 2 }) => (
    <Box style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
      <Typography style={{ fontSize: '0.64rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{title}</Typography>
      <Box style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>{children}</Box>
    </Box>
  );

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden', maxHeight: '92vh' } }}>
      {/* Header */}
      <Box style={{
        padding: '16px 22px', background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
        borderBottom: '1px solid #fed7aa', display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <Box style={{ width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LocalGasStationIcon />
        </Box>
        <Box style={{ flex: 1 }}>
          <Typography style={{ fontSize: '1.02rem', fontWeight: 800, color: '#9a3412' }}>
            {T('Relatório de Entrega', 'Delivery Report')} · {otRef}
          </Typography>
          <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {T('Distribuição de combustível', 'Fuel distribution')} · {supplier}
          </Typography>
        </Box>
        {/* Selo entrega dividida (2 P.A.) */}
        {isSplitOT && (
          <Box style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999,
            background: '#ffedd5', color: '#9a3412', border: '1px solid #fdba74', fontSize: '0.78rem', fontWeight: 800,
          }}>🔀 {T('2 P.A.', '2 points')}</Box>
        )}
        {/* Estado — "Entrega Efetuada" (item 1) */}
        <Box style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999,
          background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.78rem', fontWeight: 800,
        }}>✓ {T('Entrega Efetuada', 'Delivery Completed')}</Box>
        <IconButton size="small" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box style={{ padding: '18px 22px', background: '#fafafa', overflowY: 'auto' }}>
        {/* Cliente (6, 7) */}
        <Section title={T('Cliente', 'Customer')}>
          <Field label={T('Cliente', 'Customer')} value={order.deliveryName || order.finalCustomer?.name || '—'} />
          <Field label={T('Endereço do cliente', 'Customer address')} value={order.deliveryAddress} />
        </Section>

        {/* Entrega dividida por 2 P.A. — só quando aplicável */}
        {isSplitOT && (
          <Box style={{ background: '#fff', border: '1px solid #fdba74', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
            <Typography style={{ fontSize: '0.64rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>
              🔀 {T('Entrega dividida por 2 P.A.', 'Split delivery across 2 points')}
            </Typography>
            <Typography style={{ fontSize: '0.7rem', color: '#78350f', marginBottom: 10 }}>
              {T('A mesma cisterna descarregou em 2 postos de abastecimento.', 'The same cistern discharged at 2 delivery points.')}
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { tag: T('P.A. 1', 'Point 1'), name: order.deliveryName, addr: order.deliveryAddress, qty: pa1Qty },
                { tag: T('P.A. 2', 'Point 2'), name: order.deliveryName2, addr: order.deliveryAddress2, qty: pa2Qty },
              ].map((pa, i) => (
                <Box key={i} style={{ border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 12px', background: '#fff7ed' }}>
                  <Typography style={{ fontSize: '0.6rem', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5 }}>{pa.tag}</Typography>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: pa.name ? '#0f172a' : '#cbd5e1' }}>{pa.name || '—'}</Typography>
                  <Typography style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 1 }}>{pa.addr || '—'}</Typography>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#9a3412', marginTop: 5, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {pa.qty != null ? `${numFmt(pa.qty)} L` : '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
            {(pa1Qty != null || pa2Qty != null) && (
              <Typography style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b', marginTop: 8 }}>
                Σ {numFmt((pa1Qty || 0) + (pa2Qty || 0))} L {T('(litros planeados por posto)', '(planned litres per point)')}
              </Typography>
            )}
          </Box>
        )}

        {/* Carregamento (2,3,4,5 + selo nº) */}
        <Section title={T('Carregamento', 'Loading')}>
          <Field label={T('Ordem de carregamento', 'Loading order')} value={order.loadingOrderRef} mono />
          <Field label={T('Quantidade carregada', 'Loaded quantity')} value={loadedQty != null ? `${numFmt(loadedQty)} L` : null} mono />
          <Field label={T('Número do pedido', 'Request number')} value={order.loadingPedidoSnld} mono />
          <Field label={T('Documento pedido', 'Request document')} value={order.loadingDocRef} mono />
          <Field label={T('Nº do selo', 'Seal Nº')} value={order.loadingSealNumber} mono />
        </Section>

        {/* Entrega (guia, litros, medição, divergência, selo — 11,12,13,14) */}
        <Section title={T('Entrega', 'Delivery')}>
          <Field label={T('Nº da Guia de Conformidade', 'Compliance Guide Nº')} value={order.deliveryGuideRef} mono />
          <Field label={T('Litros descarregados', 'Unloaded litres')} value={unloadQty != null ? `${numFmt(unloadQty)} L` : null} mono />
          <Field label={T('Medição da cisterna (veículo)', 'Cistern measurement')} value={order.cisternMeasureLitres != null ? `${numFmt(order.cisternMeasureLitres)} L` : null} mono />
          <Field label={T('Divergência (carga − descarga)', 'Divergence')} value={divergence != null ? `${divergence > 0 ? '−' : '+'}${numFmt(Math.abs(divergence))} L` : null} mono />
          <Box style={{ gridColumn: '1 / -1' }}>
            <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{T('Confirmação do selo à chegada', 'Seal on arrival')}</Typography>
            {sealIntact == null ? (
              <Typography style={{ fontSize: '0.86rem', color: '#cbd5e1' }}>—</Typography>
            ) : (
              <Box style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 3, padding: '4px 12px', borderRadius: 999, fontSize: '0.82rem', fontWeight: 800,
                background: sealIntact ? '#dcfce7' : '#fee2e2', color: sealIntact ? '#15803d' : '#b91c1c',
                border: `1px solid ${sealIntact ? '#bbf7d0' : '#fecaca'}`,
              }}>{sealIntact ? T('✓ Chegou selado (intacto)', '✓ Arrived sealed') : T('⚠ Selo violado', '⚠ Seal violated')}</Box>
            )}
          </Box>
          {order.divergenceReason && (
            <Box style={{ gridColumn: '1 / -1' }}>
              <Field label={T('Motivo da divergência', 'Divergence reason')} value={order.divergenceReason} />
            </Box>
          )}
        </Section>

        {/* Fotos das guias (8, 9) */}
        <Section title={T('Guias de transporte', 'Transport guides')}>
          {[
            { url: loadingPhotoUrl, label: T('Guia após carregamento', 'Guide after loading') },
            { url: deliveryPhotoUrl, label: T('Guia após entrega', 'Guide after delivery') },
          ].map((p, i) => (
            <Box key={i}>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{p.label}</Typography>
              {p.url ? (
                <Box
                  onClick={() => setZoom(p)}
                  title={T('Clicar para ampliar', 'Click to enlarge')}
                  style={{ position: 'relative', cursor: 'zoom-in' }}
                >
                  <img src={p.url} alt={p.label} style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8, border: '1px solid #fed7aa', background: '#fff', display: 'block' }} />
                  <Box style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(15,23,42,0.62)', color: '#fff', borderRadius: 7, padding: '3px 5px', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700 }}>
                    <ZoomInIcon style={{ fontSize: 14 }} /> {T('ampliar', 'zoom')}
                  </Box>
                </Box>
              ) : (
                <Box style={{ height: 90, borderRadius: 8, border: '1px dashed #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '0.72rem' }}>{T('Sem foto', 'No photo')}</Box>
              )}
            </Box>
          ))}
        </Section>
      </Box>

      <Box style={{ padding: '12px 22px', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onClose} style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>{T('Fechar', 'Close')}</Button>
        <Button onClick={() => generateFuelReportPdf({ ...order, loadingPhotoUrl, deliveryPhotoUrl }, { supplier, loadedQty, unloadQty, divergence, sealIntact, otRef, isSplitOT, pa1Qty, pa2Qty })}
          variant="contained" startIcon={<PictureAsPdfIcon style={{ fontSize: 18 }} />}
          style={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff', borderRadius: 8, padding: '7px 18px' }}>
          {T('Gerar PDF', 'Generate PDF')}
        </Button>
      </Box>
    </Dialog>

    {/* Visor de ampliação (lightbox) com ZOOM. É um Dialog em ecrã inteiro
        (renderizado em portal → fica SEMPRE por cima do relatório). Clicar na
        imagem aproxima no ponto clicado; clicar no fundo escuro / ✕ fecha. */}
    <Dialog open={!!zoom} onClose={closeZoom} fullScreen
      PaperProps={{ style: { background: 'rgba(2,6,23,0.92)' } }}>
      {zoom && (
        <Box onClick={closeZoom}
          style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20 }}>
          <Box onClick={e => e.stopPropagation()}
            style={{ overflow: 'hidden', maxWidth: '94vw', maxHeight: '84vh', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
            <img
              src={zoom.url} alt={zoom.label}
              onClick={(e) => {
                if (zoomScale > 1) { setZoomScale(1); return; }
                const r = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - r.left) / r.width) * 100;
                const y = ((e.clientY - r.top) / r.height) * 100;
                setZoomOrigin({ x, y });
                setZoomScale(2.8);
              }}
              style={{ maxWidth: '94vw', maxHeight: '84vh', objectFit: 'contain', display: 'block',
                transform: `scale(${zoomScale})`, transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                transition: 'transform 0.15s ease', cursor: zoomScale > 1 ? 'zoom-out' : 'zoom-in' }}
            />
          </Box>
          <Typography style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
            {zoom.label} · {zoomScale > 1
              ? T('clicar para reduzir', 'click to zoom out')
              : T('clicar na imagem para aproximar', 'click image to zoom in')}
          </Typography>
          <IconButton onClick={closeZoom}
            style={{ position: 'absolute', top: 14, right: 14, color: '#fff', background: 'rgba(255,255,255,0.14)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Dialog>
    </>
  );
}

// ─── PDF (A4) — guia de entrega de combustível ─────────────────────
function generateFuelReportPdf(order, ctx) {
  const { supplier, loadedQty, unloadQty, divergence, sealIntact, otRef, isSplitOT, pa1Qty, pa2Qty } = ctx;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const PAGE_W = 210, PAGE_H = 297, MARGIN = 15, CW = PAGE_W - 2 * MARGIN;
  let y = MARGIN;
  const ensure = (n) => { if (y + n > PAGE_H - MARGIN) { doc.addPage(); y = MARGIN; } };
  const fmt = (v) => (v != null && v !== '' ? Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 2 }) : '—');

  // Cabeçalho
  doc.setFillColor(249, 115, 22); doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text('Relatório de Entrega — Combustível', MARGIN, 14);
  doc.setFontSize(10); doc.setTextColor(255, 237, 213);
  doc.text(`${otRef}  ·  ${supplier}  ·  ENTREGA EFETUADA${isSplitOT ? '  ·  2 P.A.' : ''}`, MARGIN, 19);
  y = 30;

  function section(title) {
    ensure(11); doc.setFillColor(255, 247, 237); doc.rect(MARGIN, y - 1, CW, 7, 'F');
    doc.setTextColor(154, 52, 18); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text(title.toUpperCase(), MARGIN + 2, y + 4); y += 11;
  }
  function row(label, value) {
    ensure(7); doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(String(label), MARGIN + 2, y);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
    doc.text(String(value ?? '—'), MARGIN + CW / 2, y); y += 6.5;
  }

  section('Cliente');
  row('Cliente', order.deliveryName || order.finalCustomer?.name || '—');
  row('Endereço', order.deliveryAddress || '—');

  if (isSplitOT) {
    section('Entrega dividida por 2 P.A.');
    row('P.A. 1', order.deliveryName || '—');
    row('Morada P.A. 1', order.deliveryAddress || '—');
    row('Litros P.A. 1', pa1Qty != null ? `${fmt(pa1Qty)} L` : '—');
    row('P.A. 2', order.deliveryName2 || '—');
    row('Morada P.A. 2', order.deliveryAddress2 || '—');
    row('Litros P.A. 2', pa2Qty != null ? `${fmt(pa2Qty)} L` : '—');
    if (pa1Qty != null || pa2Qty != null) row('Σ Litros planeados', `${fmt((pa1Qty || 0) + (pa2Qty || 0))} L`);
  }

  section('Carregamento');
  row('Ordem de carregamento', order.loadingOrderRef || '—');
  row('Quantidade carregada', loadedQty != null ? `${fmt(loadedQty)} L` : '—');
  row('Número do pedido (SNLD)', order.loadingPedidoSnld || '—');
  row('Documento pedido', order.loadingDocRef || '—');
  row('Nº do selo', order.loadingSealNumber || '—');

  section('Entrega');
  row('Nº Guia de Conformidade', order.deliveryGuideRef || '—');
  row('Litros descarregados', unloadQty != null ? `${fmt(unloadQty)} L` : '—');
  row('Medição da cisterna (veículo)', order.cisternMeasureLitres != null ? `${fmt(order.cisternMeasureLitres)} L` : '—');
  row('Divergência (carga − descarga)', divergence != null ? `${divergence > 0 ? '−' : '+'}${fmt(Math.abs(divergence))} L` : '—');
  row('Selo à chegada', sealIntact == null ? '—' : (sealIntact ? 'Chegou selado (intacto)' : 'SELO VIOLADO'));
  if (order.divergenceReason) row('Motivo divergência', order.divergenceReason);

  // Fotos das guias
  const photos = [
    { url: order.loadingPhotoUrl, label: 'Guia após carregamento' },
    { url: order.deliveryPhotoUrl, label: 'Guia após entrega' },
  ].filter(p => p.url && /^data:image/.test(p.url));
  if (photos.length) {
    section('Guias de transporte');
    for (const p of photos) {
      ensure(70);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(p.label, MARGIN + 2, y); y += 4;
      try { doc.addImage(p.url, 'JPEG', MARGIN + 2, y, 80, 60); } catch (_) { /* formato não suportado */ }
      y += 64;
    }
  }

  doc.save(`Relatorio_Entrega_${String(otRef).replace(/[^\w-]/g, '_')}.pdf`);
}
